<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { IconUsers, IconUserPlus } from '@tabler/icons-vue'
import { localDB } from '@/migrate.ts'
import {
  eventPeopleTable,
  peopleTable,
  personAccountLinksTable,
  personRelationshipsTable,
} from '../../client-db/src/db/schema'
import { asc, eq } from 'drizzle-orm'
import {
  getInitials,
  inferRelationLabel,
  inferRelationship,
  isLegacyAvatarUrl,
  loadPeopleUiMetaFromStorage,
  pickAvatarForPerson,
  reserveAvatarSlotForPerson,
  savePeopleUiMetaToStorage,
} from '@/utils/people-avatar.ts'
import type { AvatarCategory, PersonUiMeta, Relationship } from '@/utils/people-avatar.ts'

type PersonRow = {
  id: number
  name: string
  birthDate: Date | null
  notes: string | null
  moments: number
  relationship: Relationship
  relationLabel: string
  avatarUrl: string | null
  immediateFamilyRank: number
  isSelf: boolean
}

const relationshipOptions: Relationship[] = [
  'Immediate Family',
  'Extended Family',
  'Friends',
  'Coworkers',
  'Other',
]

const people = ref<PersonRow[]>([])
const loading = ref(false)
const showAddForm = ref(false)
const nameInput = ref('')
const relationshipInput = ref<Relationship>('Immediate Family')
const notesInput = ref('')
const relationLabelInput = ref('')
const useProfilePhotoInput = ref(true)
const formError = ref<string | null>(null)
const peopleUiMeta = ref<Record<string, PersonUiMeta>>({})
const isLegacyPeopleUiMeta = ref(false)
const showPersonSheet = ref(false)
const selectedPerson = ref<PersonRow | null>(null)
const birthdayDateInput = ref('')
const birthdayTimeInput = ref('')
const savingPerson = ref(false)

function formatDateInput(value: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) return ''
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatTimeInput(value: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) return ''
  const hours = String(value.getHours()).padStart(2, '0')
  const minutes = String(value.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

function formatBirthdayLabel(value: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) return 'Birthday not set'
  return value.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function openPersonSheet(person: PersonRow) {
  selectedPerson.value = person
  birthdayDateInput.value = formatDateInput(person.birthDate)
  birthdayTimeInput.value = formatTimeInput(person.birthDate)
  formError.value = null
  showPersonSheet.value = true
}

function closePersonSheet() {
  showPersonSheet.value = false
  selectedPerson.value = null
  birthdayDateInput.value = ''
  birthdayTimeInput.value = ''
}

async function savePersonBirthday() {
  if (!selectedPerson.value) return

  const trimmedDate = birthdayDateInput.value.trim()
  let nextBirthDate: Date | null = null

  if (trimmedDate) {
    const nextIso = `${trimmedDate}T${birthdayTimeInput.value.trim() || '00:00'}`
    const parsed = new Date(nextIso)
    if (Number.isNaN(parsed.getTime())) {
      formError.value = 'Please enter a valid birthday.'
      return
    }
    nextBirthDate = parsed
  }

  savingPerson.value = true
  formError.value = null
  try {
    await localDB
      .update(peopleTable)
      .set({
        birthDate: nextBirthDate,
      })
      .where(eq(peopleTable.id, selectedPerson.value.id))

    await loadPeople()
    closePersonSheet()
  } catch (error) {
    console.error('Failed saving person birthday:', error)
    formError.value = 'Failed to save birthday.'
  } finally {
    savingPerson.value = false
  }
}

function buildAvatarSequenceFromCurrentPeople(): Map<AvatarCategory, number> {
  const avatarSequenceByCategory = new Map<AvatarCategory, number>()
  for (const person of people.value) {
    if (!person.avatarUrl) continue
    reserveAvatarSlotForPerson(
      person.name,
      person.notes,
      person.relationLabel,
      person.relationship,
      avatarSequenceByCategory,
    )
  }
  return avatarSequenceByCategory
}

function loadPeopleUiMeta() {
  const parsed = loadPeopleUiMetaFromStorage()
  peopleUiMeta.value = parsed
  isLegacyPeopleUiMeta.value = Object.values(parsed).some(
    (meta) => typeof meta?.avatarUrl === 'string' && isLegacyAvatarUrl(meta.avatarUrl),
  )
}

function savePeopleUiMeta() {
  savePeopleUiMetaToStorage(peopleUiMeta.value)
}

async function loadPeople() {
  loading.value = true
  formError.value = null

  try {
    const [peopleRows, eventPeopleRows, selfLinkRows, relationshipRows] = await Promise.all([
      localDB
        .select({
          id: peopleTable.id,
          fullName: peopleTable.fullName,
          firstName: peopleTable.firstName,
          lastName: peopleTable.lastName,
          birthDate: peopleTable.birthDate,
          notes: peopleTable.notes,
        })
        .from(peopleTable)
        .orderBy(asc(peopleTable.fullName)),
      localDB.select({ personId: eventPeopleTable.personId }).from(eventPeopleTable),
      localDB
        .select({ personId: personAccountLinksTable.personId })
        .from(personAccountLinksTable)
        .where(eq(personAccountLinksTable.status, 'SELF')),
      localDB
        .select({
          subjectPersonId: personRelationshipsTable.subjectPersonId,
          targetPersonId: personRelationshipsTable.targetPersonId,
          relationshipType: personRelationshipsTable.relationshipType,
        })
        .from(personRelationshipsTable),
    ])

    const momentCounts = new Map<number, number>()
    for (const row of eventPeopleRows) {
      const current = momentCounts.get(row.personId) ?? 0
      momentCounts.set(row.personId, current + 1)
    }

    const selfIds = new Set<number>(selfLinkRows.map((row) => row.personId))
    const spouseIds = new Set<number>()
    const childIds = new Set<number>()
    for (const row of relationshipRows) {
      if (row.relationshipType === 'SPOUSE') {
        if (selfIds.has(row.subjectPersonId)) spouseIds.add(row.targetPersonId)
        if (selfIds.has(row.targetPersonId)) spouseIds.add(row.subjectPersonId)
      }
      if (row.relationshipType === 'CHILD' && selfIds.has(row.subjectPersonId)) {
        childIds.add(row.targetPersonId)
      }
    }

    let didUpdateMeta = false
    const avatarSequenceByCategory = new Map<AvatarCategory, number>()
    people.value = peopleRows.map((person) => {
      const fallbackName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim()
      const resolvedName = person.fullName || fallbackName || `Person ${person.id}`
      const existingMeta = peopleUiMeta.value[String(person.id)]

      if (!existingMeta) {
        const inferredRelationship = inferRelationship(resolvedName, person.notes)
        const inferredRelationLabel = inferRelationLabel(
          resolvedName,
          person.notes,
          inferredRelationship,
        )
        peopleUiMeta.value[String(person.id)] = {
          relationship: inferredRelationship,
          relationLabel: inferredRelationLabel,
          avatarUrl: pickAvatarForPerson(
            resolvedName,
            person.notes,
            inferredRelationLabel,
            inferredRelationship,
            avatarSequenceByCategory,
          ),
        }
        didUpdateMeta = true
      } else {
        const normalizedRelationship =
          existingMeta.relationship || inferRelationship(resolvedName, person.notes)
        const normalizedRelationLabel =
          existingMeta.relationLabel ||
          inferRelationLabel(resolvedName, person.notes, normalizedRelationship)
        const noProfilePhotoRequested = /\b(no profile photo|no photo)\b/.test(
          `${resolvedName} ${person.notes || ''} ${normalizedRelationLabel}`.toLowerCase(),
        )
        const shouldRegenerateAvatar =
          !noProfilePhotoRequested &&
          (typeof existingMeta.avatarUrl === 'undefined' ||
            isLegacyAvatarUrl(existingMeta.avatarUrl) ||
            (isLegacyPeopleUiMeta.value && existingMeta.avatarUrl === null))
        const normalizedMeta: PersonUiMeta = {
          relationship: normalizedRelationship,
          relationLabel: normalizedRelationLabel,
          avatarUrl: noProfilePhotoRequested
            ? null
            : shouldRegenerateAvatar
              ? pickAvatarForPerson(
                  resolvedName,
                  person.notes,
                  normalizedRelationLabel,
                  normalizedRelationship,
                  avatarSequenceByCategory,
                )
              : existingMeta.avatarUrl,
        }

        if (!shouldRegenerateAvatar && !noProfilePhotoRequested && normalizedMeta.avatarUrl) {
          reserveAvatarSlotForPerson(
            resolvedName,
            person.notes,
            normalizedRelationLabel,
            normalizedRelationship,
            avatarSequenceByCategory,
          )
        }

        if (
          normalizedMeta.relationship !== existingMeta.relationship ||
          normalizedMeta.relationLabel !== existingMeta.relationLabel ||
          normalizedMeta.avatarUrl !== existingMeta.avatarUrl
        ) {
          peopleUiMeta.value[String(person.id)] = normalizedMeta
          didUpdateMeta = true
        }
      }

      const nextMeta = peopleUiMeta.value[String(person.id)]

      return {
        id: person.id,
        name: resolvedName,
        birthDate: person.birthDate ?? null,
        notes: person.notes ?? null,
        moments: momentCounts.get(person.id) ?? 0,
        relationship: nextMeta.relationship,
        relationLabel: nextMeta.relationLabel,
        avatarUrl: nextMeta.avatarUrl,
        immediateFamilyRank: selfIds.has(person.id)
          ? 0
          : spouseIds.has(person.id)
            ? 1
            : childIds.has(person.id)
              ? 2
              : 99,
        isSelf: selfIds.has(person.id),
      }
    })

    if (didUpdateMeta) {
      savePeopleUiMeta()
    }
  } catch (error) {
    console.error('Failed loading people:', error)
    formError.value = 'Failed to load people.'
  } finally {
    loading.value = false
  }
}

async function addPerson() {
  const fullName = nameInput.value.trim()
  if (!fullName) {
    formError.value = 'Enter a name.'
    return
  }

  const parts = fullName.split(/\s+/).filter(Boolean)
  const firstName = parts[0] ?? null
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null

  try {
    const [createdPerson] = await localDB
      .insert(peopleTable)
      .values({
        firstName,
        lastName,
        fullName,
        notes: notesInput.value.trim() || null,
      })
      .returning({ id: peopleTable.id })

    if (createdPerson?.id) {
      const relationLabel =
        relationLabelInput.value.trim() ||
        inferRelationLabel(fullName, notesInput.value, relationshipInput.value)
      const avatarSequenceByCategory = buildAvatarSequenceFromCurrentPeople()
      peopleUiMeta.value[String(createdPerson.id)] = {
        relationship: relationshipInput.value,
        relationLabel,
        avatarUrl: useProfilePhotoInput.value
          ? pickAvatarForPerson(
              fullName,
              notesInput.value,
              relationLabel,
              relationshipInput.value,
              avatarSequenceByCategory,
            )
          : null,
      }
      savePeopleUiMeta()
    }

    nameInput.value = ''
    notesInput.value = ''
    relationLabelInput.value = ''
    relationshipInput.value = 'Immediate Family'
    useProfilePhotoInput.value = true
    showAddForm.value = false
    formError.value = null
    await loadPeople()
  } catch (error) {
    console.error('Failed adding person:', error)
    formError.value = 'Failed to add person.'
  }
}

const groupedPeople = computed(() =>
  relationshipOptions
    .map((relationship) => ({
      relationship,
      members: people.value
        .filter((person) => person.relationship === relationship)
        .sort((a, b) => {
          if (relationship === 'Immediate Family') {
            if (a.immediateFamilyRank !== b.immediateFamilyRank) {
              return a.immediateFamilyRank - b.immediateFamilyRank
            }
          }
          return a.name.localeCompare(b.name)
        }),
    }))
    .filter((group) => group.members.length > 0),
)

onMounted(() => {
  loadPeopleUiMeta()
  void loadPeople()
})
</script>

<template>
  <main class="min-h-full p-4 text-base-content">
    <section class="flex flex-col gap-2">
      <div class="flex items-center gap-4">
        <div>
          <div class="rounded-xl bg-primary/10 p-2 text-primary">
            <IconUsers class="h-8 w-8" />
          </div>
        </div>
        <div>
          <h1 class="text-2xl md:text-3xl font-semibold">People</h1>
        </div>
        <div class="grow flex justify-end">
          <button class="btn btn-primary rounded-full px-6" @click="showAddForm = !showAddForm">
            <IconUserPlus class="pr-2" />
            Add
          </button>
        </div>
      </div>
      <p class="text-sm text-base-content/70">
        Here's all the people you care about! Check out what they've shared with you.
      </p>
    </section>

    <div class="h-2 my-2 rounded-full bg-primary" />

    <div class="mx-auto max-w-md rounded-sm p-3">
      <div v-if="showAddForm" class="mb-4 rounded-xl border border-base-300 bg-base-100 p-3">
        <div class="mb-2 text-sm font-semibold text-base-content">Add person</div>
        <div class="flex flex-col gap-2">
          <input
            v-model="nameInput"
            type="text"
            placeholder="Full name"
            class="input input-bordered w-full bg-base-100 text-base-content"
            @keyup.enter="addPerson"
          />
          <select
            v-model="relationshipInput"
            class="select select-bordered w-full bg-base-100 text-base-content"
          >
            <option
              v-for="relationship in relationshipOptions"
              :key="relationship"
              :value="relationship"
            >
              {{ relationship }}
            </option>
          </select>
          <textarea
            v-model="notesInput"
            class="textarea textarea-bordered w-full bg-base-100 text-base-content"
            placeholder="Notes (optional)"
          />
          <input
            v-model="relationLabelInput"
            type="text"
            placeholder="Relation label (for example: Wife, Husband, Son, Daughter)"
            class="input input-bordered w-full bg-base-100 text-base-content"
          />
          <label class="label cursor-pointer justify-start gap-2 px-0">
            <input v-model="useProfilePhotoInput" type="checkbox" class="checkbox checkbox-sm" />
            <span class="label-text">Use a profile picture</span>
          </label>
          <button class="btn btn-primary" @click="addPerson">Save</button>
        </div>
      </div>

      <div
        v-if="formError"
        class="mb-3 rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
      >
        {{ formError }}
      </div>

      <div v-if="loading" class="text-sm text-base-content/70">Loading people...</div>

      <section v-for="group in groupedPeople" :key="group.relationship" class="mb-5">
        <h2 class="mb-2 text-xl font-semibold text-base-content">{{ group.relationship }}</h2>
        <div class="grid grid-cols-2 gap-y-6 gap-x-4">
          <article
            v-for="person in group.members"
            :key="person.id"
            class="card cursor-pointer border border-base-300 bg-base-100 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            @click="openPersonSheet(person)"
          >
            <div class="card-body items-center p-4 text-center">
              <img
                v-if="person.avatarUrl"
                :src="person.avatarUrl"
                :alt="`${person.name} profile photo`"
                class="mb-3 h-[112px] w-[112px] rounded-full border border-base-300 object-cover shadow-sm"
              />
              <div
                v-else
                class="mb-3 h-[112px] w-[112px] rounded-full border border-base-300 bg-base-200 object-cover shadow-sm flex items-center justify-center text-2xl font-semibold"
              >
                {{ getInitials(person.name) }}
              </div>
              <div class="text-lg leading-tight font-semibold text-base-content">
                {{ person.name }}
              </div>
              <div
                v-if="person.isSelf"
                class="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
              >
                Self
              </div>
              <div class="text-xs font-semibold tracking-wide text-primary/80">
                {{ person.relationLabel }}
              </div>
              <div class="text-xs text-base-content/70">
                {{ formatBirthdayLabel(person.birthDate) }}
              </div>
              <div class="text-xs font-semibold tracking-wide text-base-content/65">
                {{ person.moments }} MOMENTS
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div class="modal modal-bottom sm:modal-middle" :class="{ 'modal-open': showPersonSheet }">
      <div class="modal-box">
        <h3 class="text-lg font-semibold">{{ selectedPerson?.name }}</h3>
        <p class="mt-1 text-sm text-base-content/70">
          {{ selectedPerson?.isSelf ? 'This birthday is used to help place events on the timeline.' : 'Set an optional birthday for this person.' }}
        </p>
        <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="form-control w-full">
            <span class="label-text mb-1 text-sm">Birthday</span>
            <input
              v-model="birthdayDateInput"
              type="date"
              class="input input-bordered w-full bg-base-100 text-base-content"
            />
          </label>
          <label class="form-control w-full">
            <span class="label-text mb-1 text-sm">Birth time (optional)</span>
            <input
              v-model="birthdayTimeInput"
              type="time"
              class="input input-bordered w-full bg-base-100 text-base-content"
            />
          </label>
        </div>
        <div class="modal-action">
          <button class="btn" @click="closePersonSheet">Cancel</button>
          <button class="btn btn-primary" :disabled="savingPerson" @click="savePersonBirthday">
            {{ savingPerson ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closePersonSheet">
        <button>close</button>
      </div>
    </div>
  </main>
</template>
